import React, { useCallback } from "react";
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { TagsInput } from ".";
import { kColors } from "../kColors";
import { CancelIcon } from "./impComponents/CancelIcon";

type tVariantProps = {
  myIndex: number;
  myDelete: (myIndex: number) => void;
  myZIndex: number;
};

type tpVariant = (props: tVariantProps) => React.ReactElement;
const VariantOption: tpVariant = ({ myIndex, myDelete, myZIndex }) => {
  const variantOptions = useSelector((state) => state.ProductReducer.variantOptions);
  const dispatch = useAppDispatch();

  const _handleTextChange = useCallback(
    (myValue) => {
      dispatch({
        type: "UPDATE_VARIANT_OPTION_ARRAY",
        index: myIndex,
        singleVariantOptionPart: { name: myValue },
      });
    },
    [dispatch, myIndex],
  );
  const _handleTagsChange = useCallback(
    (myText: Array<string>) => {
      dispatch({
        type: "UPDATE_VARIANT_OPTION_ARRAY",
        index: myIndex,
        singleVariantOptionPart: { labels: myText },
      });
    },
    [dispatch, myIndex],
  );

  const _handleOnPressDelete = useCallback(() => myDelete(myIndex), [myIndex, myDelete]);

  return (
    <View style={[styles.P08_01_VariantContainer, { zIndex: myZIndex }]}>
      <View style={styles.P08_01_VariantIndexArea}>
        <Text>{myIndex + 1}</Text>
      </View>
      <View style={styles.P08_01_VariantFormArea}>
        <View style={[styles.P08_01_FormRow, styles.zIndexTen]}>
          <View style={styles.P08_01_FormItem}>
            <Text style={styles.P08_01_InputHeader}>Option</Text>
            <TextInput
              onChangeText={_handleTextChange}
              style={styles.P08_01_Input}
              value={variantOptions[myIndex].name}
              placeholder="Color, size, etc."
              placeholderTextColor={kColors.lightMed}
            />
          </View>
          <View style={styles.P08_01_VariantDescription}>
            <Text style={styles.P08_01_InputHeader}>Values</Text>
            <TagsInput
              onChangeTags={_handleTagsChange}
              inputStyleNoTags={styles.noTagTextInput}
              inputStyleWithTags={styles.withTagTextInput}
              value={variantOptions[myIndex]?.labels?.join(",") ?? ""}
              placeholder="Enter a list of values separated by commas (i.e. red, blue)..."
              placeholderTextColor={kColors.lightMed}
              highlightStyle={styles.gTag}
              deleteStyle={styles.gTagDelete}
              allowSpace
            />
          </View>
          <TouchableOpacity style={styles.variantDeleteView} onPress={_handleOnPressDelete}>
            <CancelIcon />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default VariantOption;

const styles = StyleSheet.create({
  P08_01_FormItem: {
    flex: 1,
    marginRight: 8 * kStyleConst.kSpacing,
  },
  P08_01_FormRow: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  P08_01_Input: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: kStyleConst.kSpacing,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P08_01_VariantContainer: {
    flexDirection: "row",
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  P08_01_VariantDescription: {
    flex: 2,
    marginRight: 8 * kStyleConst.kSpacing,
  },
  P08_01_VariantFormArea: {
    borderLeftColor: kColors.brandPink,
    borderLeftWidth: 1,
    flex: 1,
    paddingLeft: 8 * kStyleConst.kSpacing,
  },
  P08_01_VariantIndexArea: {
    width: 9 * kStyleConst.kSpacing,
  },
  gTag: {
    alignItems: "center",
    backgroundColor: kColors.white,
    borderColor: kColors.brandPink,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  gTagDelete: {
    alignItems: "center",
    backgroundColor: kColors.brandPink,
    borderColor: kColors.brandPink,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  noTagTextInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: kStyleConst.kSubHeaderHeight,
    marginTop: kStyleConst.kSpacing,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  variantDeleteView: {
    ...kStyleConst.kPlusMinusButton,
    alignSelf: "center",
  },
  withTagTextInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: kStyleConst.kSubHeaderHeight,
    marginTop: kStyleConst.kSpacing,
    outlineColor: kColors.transparent,
    paddingBottom: 2 * kStyleConst.kSpacing,
    paddingLeft: kStyleConst.kSpacing,
    paddingTop: kStyleConst.kSpacing - 1,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  zIndexTen: {
    zIndex: 10,
  },
});
